import { useCatalog } from "catalog/Catalog.context";
import {
  CatalogLink,
  CatalogAutoplayButton,
  CatalogPagingButton,
} from "catalog/CatalogNavigations";
import { AnimatePresence } from "framer-motion";
import { Anime, Image } from "components/elements";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export default function GlobalNavigation() {
  return (
    <div className="pointer-events-none absolute left-0 top-0 z-[9999] h-full w-full">
      <Header />
      <LnbBar />
    </div>
  );
}

function Header() {
  const { pageName, nestedPageName } = useParams();
  const { activePageInfo } = useCatalog();

  if (!activePageInfo) return null;

  const { gnb, depth1 } = activePageInfo;

  const classNames = {
    buttonContainer: twMerge("relative z-10 flex flex-shrink-0 flex-col items-center"),
    button: twMerge("flex h-[72px] w-[50px] items-center justify-center"),
    buttonDivider: twMerge("h-[1px] w-[25px] bg-[#AEAEAE] block"),
    gnb: {
      container: twMerge("h-[720px] w-full bg-[#4B4B4B] flex flex-col items-center"),
      button: (active) =>
        twMerge(
          "flex items-center justify-center w-full h-full uppercase text-[#7B7B7B] text-[20px] writing-mode:verticalLr font-bold",
          "hover:text-opacity-50",
          depth1 === active && "text-[#61ABFF]",
        ),
      buttonText: "-rotate-90",
    },
  };

  return (
    <header
      className={twMerge(
        "pointer-events-auto relative flex h-full w-[100px] flex-col items-center justify-between py-4",
      )}
    >
      <AnimatePresence>
        {gnb && (
          <Anime
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            className={twMerge("absolute left-0 top-0 z-0 h-full w-full bg-[#4E484A]")}
          />
        )}
      </AnimatePresence>
      <div className={classNames.buttonContainer}>
        <CatalogLink to="/" className={classNames.button}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="38.071"
            height="33.422"
            viewBox="0 0 38.071 33.422"
          >
            <path
              d="M17.934,34.04H12a3,3,0,0,1-3-3V19H5.468a2,2,0,0,1-1.291-3.528L21.209,1.09a2,2,0,0,1,2.581,0L40.822,15.472A2,2,0,0,1,39.532,19H36V31.038a3,3,0,0,1-3,3H26.935v-9a1,1,0,0,0-1-1h-7a1,1,0,0,0-1,1v9h0Z"
              transform="translate(-3.464 -0.618)"
              fill="#858585"
            />
          </svg>
        </CatalogLink>
      </div>

      <AnimatePresence>
        {gnb && (
          <Anime
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            className={classNames.gnb.container}
          >
            <CatalogLink to="platform-intro" className={classNames.gnb.button("platform")}>
              <span className={classNames.gnb.buttonText}>Platform</span>
            </CatalogLink>
            <i className={classNames.buttonDivider} />
            <CatalogLink to="product-intro/one-key" className={classNames.gnb.button("product")}>
              <span className={classNames.gnb.buttonText}>Product</span>
            </CatalogLink>
            <i className={classNames.buttonDivider} />
            <CatalogLink to="manual/1" className={classNames.gnb.button("manual")}>
              <span className={classNames.gnb.buttonText}>Manual</span>
            </CatalogLink>
            <i className={classNames.buttonDivider} />
            <CatalogLink to="company-vision" className={classNames.gnb.button("company")}>
              <span className={classNames.gnb.buttonText}>Company</span>
            </CatalogLink>
          </Anime>
        )}
      </AnimatePresence>

      <div className={classNames.buttonContainer}>
        <CatalogLink to="catalog-index" className={classNames.button}>
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="22" viewBox="0 0 28 22">
            <rect width="28" height="4" fill={"#858585"} />
            <rect width="28" height="4" transform="translate(0 9)" fill={"#858585"} />
            <rect width="28" height="4" transform="translate(0 18)" fill={"#858585"} />
          </svg>
        </CatalogLink>
      </div>
    </header>
  );
}

function LnbBar() {
  const { lang } = useParams();
  const { activePageInfo } = useCatalog();

  if (!activePageInfo) return null;

  const { depth1, depth2 } = activePageInfo;

  const classNames = {
    wrapper: twMerge(
      "absolute top-0 left-0 w-full h-[97px] flex justify-end *:pointer-events-auto items-center pr-[30px]",
    ),
    lnb: {
      container: twMerge("flex text-[20px]"),
      button: (active) =>
        twMerge(
          "text-[#AEAEAE] px-[75px] relative after:w-[1px] after:h-[0.8em] after:bg-[#AEAEAE] after:absolute after:top-1/2 after:right-0 after:-translate-y-1/2 last:after:hidden hover:opacity-50",
          active === depth2 && "text-[#4B4B4B] font-bold",
        ),
    },
    controls: {
      container: twMerge("flex gap-[4px]"),
      button: twMerge(
        "flex h-[50px] w-[50px] items-center justify-center text-xl font-bold disabled:opacity-50",
      ),
    },
  };

  const lnb = {
    platform: [
      { to: "platform-overview", title: { en: "Summary", ko: "개요" }, key: "overview" },
      { to: "platform-features", title: { en: "Features", ko: "특징과 장점" }, key: "features" },
      {
        to: "platform-applications",
        title: { en: "Field of Application", ko: "적용 분야" },
        key: "applications",
      },
      { to: "platform-cases/1", title: { en: "Case Studies", ko: "도입 사례" }, key: "cases" },
    ],
    product: [
      {
        to: "product-digital-key",
        title: { en: "Smart Digital Key", ko: "스마트 디지털 키" },
        key: "digital-key",
      },
      {
        to: "product-digital-lock",
        title: { en: "Smart Digital Lock", ko: "스마트 디지털 락" },
        key: "digital-lock",
      },
      { to: "product-lineup", title: { en: "Product Line-up", ko: "제품 라인업" }, key: "lineup" },
    ],
    company: [
      { to: "company-vision", title: { en: "Vision & Strategy" }, key: "vision" },
      { to: "company-history/1", title: { en: "History" }, key: "history" },
      {
        to: "company-certificate",
        title: { en: "Authentication Certificate" },
        key: "certificate",
      },
      { to: "company-partners", title: { en: "Partners" }, key: "partners" },
    ],
  };

  return (
    <div className={classNames.wrapper}>
      <nav className={classNames.lnb.container}>
        {lnb[depth1]?.map((item) => (
          <CatalogLink key={item.key} to={item.to} className={classNames.lnb.button(item.key)}>
            {item.title[lang]}
          </CatalogLink>
        ))}
      </nav>
      <nav className={classNames.controls.container}>
        <CatalogPagingButton direction="prev" className={classNames.controls.button}>
          <svg xmlns="http://www.w3.org/2000/svg" width="39" height="39" viewBox="0 0 39 39">
            <g fill="none" stroke="#858585" strokeWidth="1.5">
              <circle cx="19.5" cy="19.5" r="19.5" stroke="none" />
              <circle cx="19.5" cy="19.5" r="18.75" fill="none" />
            </g>
            <path
              d="M8.77,16.011.292,8.152,8.77.292"
              transform="translate(9.435 11.348)"
              fill="none"
              stroke="#858585"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
            />
            <line
              x1="19.549"
              transform="translate(9.725 19.5)"
              fill="none"
              stroke="#858585"
              strokeLinecap="round"
              strokeWidth="1.5"
            />
          </svg>
        </CatalogPagingButton>
        <CatalogPagingButton direction="next" className={classNames.controls.button}>
          <svg xmlns="http://www.w3.org/2000/svg" width="39" height="39" viewBox="0 0 39 39">
            <g fill="none" stroke="#858585" strokeWidth="1.5">
              <circle cx="19.5" cy="19.5" r="19.5" stroke="none" />
              <circle cx="19.5" cy="19.5" r="18.75" fill="none" />
            </g>
            <path
              d="M.292,16.011,8.77,8.152.292.292"
              transform="translate(20.504 11.348)"
              fill="none"
              stroke="#858585"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
            />
            <line
              x2="19.549"
              transform="translate(9.727 19.5)"
              fill="none"
              stroke="#858585"
              strokeLinecap="round"
              strokeWidth="1.5"
            />
          </svg>
        </CatalogPagingButton>
      </nav>
    </div>
  );
}
